import { useEffect, useRef, useState } from 'react';
import { api, zeitpunkt, type Anmelder, datum, heuteIso} from '../api.ts';
import { Fehlerkasten, ausFehler, amTelefon, type Fehlerlage } from '../teile/Formular.tsx';
import { Passwortregeln, usePasswortvorgabe } from '../teile/Passwortregeln.tsx';
import Abwurf from '../teile/Abwurf.tsx';
import { Gesicht } from '../teile/Person.tsx';
import {
  passkeyMoeglich, passkeyAnlegen, passkeyAntworten, passkeyFehler,
} from '../teile/passkey.ts';
import { Codefelder } from '../teile/Codefelder.tsx';
import { vollstaendig } from '../teile/codefelder.ts';
import { useFrisch } from '../teile/Frisch.tsx';
import { fragen } from '../teile/rueckfrage.tsx';

// ============================================================================
// Der zweite Faktor.
//
// Marvin, 23.08.2026:
//   "zur 2FA dachte ich wir binden den google autentifikator ein."
//
// Zwei Bildschirme, beide bewusst schmal: wer hier steht, hat genau eine
// Aufgabe und soll nichts anderes sehen.
//
//   Abfrage      nach dem Passwort, sechs Ziffern
//   Einrichtung  QR-Code, erster Code, dann die Wiederherstellungscodes
// ============================================================================

/** Nach dem Passwort: die sechs Ziffern. */
export function FaktorAbfrage({ fertig }: { fertig: () => void }) {
  const [code, setCode] = useState('');
  /* DER HINWEIS GEHT NICHT MEHR AUF DEM WEG ZUM BILDSCHIRM VERLOREN.
     Hier stand `useState<string | null>` und daneben `setFehler(f.message)`:
     ein `hinweis`, den der Server mitschickt, fiel dabei weg, und übrig
     blieb ein Satz ohne nächsten Schritt. Genau das ist am 03.09.2026 an der
     EINRICHTUNGSMASKE aufgefallen (ein fehlender `MDS_SCHLUESSEL`), und die
     ABFRAGE daneben hat es nicht mitbekommen - **eine Behebung, die nur die
     eine Maske trifft, ist eine halbe.** */
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const unterwegs = useRef(false);
  const [merken, setMerken] = useState(true);
  const [wegNotfall, setWegNotfall] = useState(false);

  /* HAT DIESER ZUGANG ÜBERHAUPT EINEN PASSKEY?
     Der Knopf erscheint nur dann. **Ein Knopf, der auf eine Fehlermeldung
     führt, ist schlimmer als kein Knopf** - man drückt ihn, bekommt einen
     roten Kasten und glaubt der Oberfläche danach nicht mehr. `/faktor/stand`
     darf auch mit halber Sitzung gefragt werden, genau dafür steht er in der
     Liste der erlaubten Wege. */
  const [hatPasskey, setHatPasskey] = useState(false);
  useEffect(() => {
    if (!passkeyMoeglich()) return;
    let weg = false;
    api.faktorStand()
      .then((d: any) => {
        if (weg) return;
        setHatPasskey((d?.faktoren ?? []).some(
          (f: any) => f.art === 'passkey' && f.bestaetigt_am));
      })
      .catch(() => { /* Ein Stand, der nicht kommt, hält nichts auf. */ });
    return () => { weg = true; };
  }, []);

  async function mitPasskey() {
    setFehler(null); setLaeuft(true);
    try {
      const start = await api.passkeyAnmeldenStart();
      const antwort = await passkeyAntworten(start);
      await api.passkeyPruefen({ ...antwort, aufgabe: start.aufgabe,
                                 geraetMerken: merken });
      fertig();
    } catch (f: any) {
      /* `passkeyFehler` deutet die Meldung des BROWSERS und fällt sonst auf
         den Text des Servers zurück; der Hinweis daneben kommt vom Server
         und geht deshalb in beiden Fällen mit. */
      setFehler({ text: passkeyFehler(f), hinweis: f?.daten?.hinweis });
    } finally { setLaeuft(false); }
  }

  /* DAS EREIGNIS IST FREIWILLIG, UND DER CODE AUCH. Diese Funktion hat zwei
     Aufrufer: den Knopf und die sechste Ziffer. Der zweite hat kein
     Formularereignis - ein `e.preventDefault()` ohne `e` wäre dort ein
     Absturz -, und er hat ausserdem den FERTIGEN Code, während `code` hier
     noch die fünf Ziffern von vorher trägt: `setzen(...)` und `abschicken()`
     laufen im selben Ereignis, und React setzt den Zustand erst danach.
     **Wer den Zustand liest, misst den Stand von vor dem Tastendruck.** */
  async function absenden(e?: React.FormEvent, mitCode?: string) {
    e?.preventDefault();
    /* NUR EINMAL ABSCHICKEN (.275). Füllt das Telefon den Code selbst ein,
       kommen die sechs Ziffern in einem Zug, und die sechste Ziffer UND der
       Knopf schickten ab, bevor `laeuft` gesetzt war. Der zweite Aufruf
       fand den Code dann verbraucht und zeigte „stimmt nicht", obwohl der
       erste durchgegangen war. Ein Merker ist sofort da, ein Zustand erst
       nach dem Zeichnen. */
    if (unterwegs.current) return;
    unterwegs.current = true;
    const wert = mitCode ?? code;
    setFehler(null);
    setLaeuft(true);
    try {
      if (wegNotfall) await api.faktorWiederherstellen(wert);
      else await api.faktorPruefen(wert, merken);
      fertig();
    } catch (f: any) {
      setFehler(ausFehler(f));
      setCode('');
    } finally {
      setLaeuft(false);
      unterwegs.current = false;
    }
  }

  return (
    <div className="anmeldeseite">
      <form className="karte anmeldekarte" onSubmit={absenden}>
        <div className="leib">
          <h2>Noch ein Schritt</h2>
          <p className="unter">
            {wegNotfall
              ? 'Einen deiner aufgeschriebenen Wiederherstellungscodes eingeben.'
              : 'Der sechsstellige Code aus deiner Authenticator-App.'}
          </p>

          {fehler && (
            <div style={{ marginBottom: 4 }}><Fehlerkasten lage={fehler} /></div>
          )}

          <label htmlFor="code">{wegNotfall ? 'Wiederherstellungscode' : 'Code'}</label>

          {/* SECHS KÄSTCHEN FÜR DEN CODE, EIN TEXTFELD FÜR DEN NOTFALL.

              Marvin, 09.09.2026: „der code soll in 6 kästchen eingetragen
              werden, nicht in einem textfeld."

              Der Wiederherstellungscode BEHÄLT sein Feld, und das ist kein
              halber Umbau: er hat die Gestalt `ABCDE-FGHIJ`, trägt Buchstaben
              und ist elf Zeichen lang. Sechs Ziffernkästchen wären dort eine
              Sackgasse - **ein Feld, dessen Eingabe gar nicht hineinpasst, ist
              schlimmer als ein unschönes.** */}
          {wegNotfall ? (
            <input
              id="code"
              autoFocus
              /* KEIN „one-time-code" (.275): iOS schlug hier den letzten Code
                 aus einer SMS vor (6 Ziffern), und der ist nie ein
                 Wiederherstellungscode. */
              autoComplete="off" autoCapitalize="characters" autoCorrect="off" spellCheck={false}
              inputMode="text"
              placeholder="ABCDE-FGHIJ"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ fontSize: 22, letterSpacing: 3, textAlign: 'center' }}
            />
          ) : (
            <Codefelder
              wert={code}
              setzen={setCode}
              gesperrt={laeuft}
              /* WER DIE SECHSTE ZIFFER TIPPT, IST FERTIG. Ein Knopf daneben
                 bleibt trotzdem stehen: eine Eingabe, die von selbst
                 abschickt, und KEIN Knopf wäre auf einem Telefon eine Seite,
                 die man nicht mehr verlassen kann, wenn das Abschicken
                 scheitert. */
              abschicken={(ganz) => { if (!laeuft) absenden(undefined, ganz); }}
            />
          )}

          {!wegNotfall && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'center',
                            marginTop: 10, fontWeight: 400 }}>
              <input type="checkbox" checked={merken}
                     onChange={(e) => setMerken(e.target.checked)} />
              Diesem Gerät vertrauen
            </label>
          )}

          {/* SECHS ZIFFERN OHNE LÜCKE, und beim Notfallcode reicht die Länge.
              `code` trägt bei den Kästchen Leerstellen, solange nicht jedes
              gefüllt ist - `length` allein liesse „1 3 56" durch. */}
          <button className="knopf haupt"
                  disabled={laeuft
                    || (wegNotfall ? code.trim().length < 6 : !vollstaendig(code))}>
            {laeuft ? 'Einen Moment' : 'Weiter'}
          </button>

          {/* DER PASSKEY IST DER STÄRKERE WEG UND STEHT TROTZDEM DANEBEN.
              Er ist an die Adresse gebunden, eine Zifferfolge nicht - aber
              ein Haus mit nur einem Weg herein sperrt beim ersten kaputten
              Telefon jemanden aus. */}
          {hatPasskey && !wegNotfall && (
            <button type="button" className="knopf" disabled={laeuft}
                    style={{ marginTop: 8 }} onClick={mitPasskey}>
              Mit Passkey anmelden
            </button>
          )}

          <p className="klein leise" style={{ marginTop: 16, marginBottom: 0 }}>
            {wegNotfall ? (
              <>
                Jeder Code gilt genau einmal.{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); setWegNotfall(false); setCode(''); }}>
                  Zurück zur App
                </a>
              </>
            ) : (
              <>
                Telefon nicht zur Hand?{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); setWegNotfall(true); setCode(''); }}>
                  Wiederherstellungscode benutzen
                </a>
              </>
            )}
          </p>
        </div>
      </form>
    </div>
  );
}


/** Einrichten: QR-Code, erster Code, Wiederherstellungscodes. */
export function FaktorEinrichtung({
  fertig, abbrechen,
}: {
  fertig: () => void;
  abbrechen?: () => void;
}) {
  const [start, setStart] = useState<any>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [kopiert, setKopiert] = useState(false);

  /* KOPIEREN PER KNOPF (.280). ORIGINALTEXT MARVIN (02.10.2026): „das
     kopieren und einfügen bei der 2FA im iphone klappt immernoch nicht."
     Auf dem iPhone markiert ein langer Druck auf den Schlüssel nur ein
     Stück davon oder den ganzen Absatz. Der Knopf legt genau den Schlüssel
     in die Zwischenablage; wo die Zwischenablage gesperrt ist, hilft der
     alte Weg über ein unsichtbares Feld. */
  async function schluesselKopieren(text: string) {
    let ok = false;
    try { await navigator.clipboard.writeText(text); ok = true; } catch { /* unten */ }
    if (!ok) {
      const t = document.createElement('textarea');
      t.value = text; t.setAttribute('readonly', '');
      t.style.position = 'fixed'; t.style.top = '0'; t.style.opacity = '0';
      document.body.appendChild(t);
      t.focus(); t.setSelectionRange(0, text.length);
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      document.body.removeChild(t);
    }
    setKopiert(ok);
  }

  /* HIER STAND `f.message`, UND GENAU DARAN IST DER 03.09.2026 GESCHEITERT.
     Der Server sagt bei einer fehlenden Einrichtung nicht nur, WAS los ist,
     sondern im `hinweis` daneben auch, was zu tun ist - und der ist auf dem
     Weg hierher verlorengegangen. Uebrig blieb ein Satz ohne naechsten
     Schritt. `ausFehler` nimmt beides mit, `Fehlerkasten` zeigt beides. */
  useEffect(() => {
    api.faktorEinrichten().then(setStart).catch((f) => setFehler(ausFehler(f)));
  }, []);

  async function bestaetigen(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null); setLaeuft(true);
    try {
      const a = await api.faktorBestaetigen(code);
      setCodes(a.wiederherstellungscodes);
    } catch (f: any) {
      setFehler(ausFehler(f)); setCode('');
    } finally { setLaeuft(false); }
  }

  if (fehler && !start) {
    return <Fehlerkasten lage={fehler} />;
  }
  if (!start) return null;

  // --- Geschafft: die Codes einmal zeigen, danach nie wieder.
  if (codes) {
    return (
      <div className="karte">
        <h3>Geschafft. Diese 10 Codes jetzt sichern.</h3>
        <div className="leib">
          <div className="hinweis" style={{ marginTop: 0 }}>
            Ausdrucken oder abschreiben und dorthin legen, wo auch der
            Firmenschlüssel liegt. Jeder Code gilt genau einmal. Sie sind der
            einzige Weg zurück, wenn das Telefon weg ist, und werden{' '}
            <b>nie wieder angezeigt</b>.
          </div>
          <div className="reihe" style={{ margin: '14px 0' }}>
            {codes.map((c) => (
              <code key={c} style={{
                fontSize: 16, letterSpacing: 1, padding: '6px 10px',
                border: '1px solid var(--rand)', borderRadius: 6,
              }}>{c}</code>
            ))}
          </div>
          <div className="reihe">
            <button className="knopf" onClick={() => {
              const w = window.open('', '_blank');
              if (!w) return;
              w.document.write(
                '<pre style="font-size:16px;line-height:2">'
                + 'MDS FTTH Wiederherstellungscodes\n'
                + datum(heuteIso()) + '\n\n'
                + codes.join('\n') + '</pre>');
              w.document.close(); w.print();
            }}>
              Drucken
            </button>
            <button className="knopf haupt" onClick={fertig}>
              Habe ich gesichert, weiter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Einrichten
  return (
    <div className="karte">
      <h3>Authenticator-App einrichten</h3>
      <div className="leib">
        <p className="klein" style={{ marginTop: 0 }}>
          In der App (Google Authenticator, Aegis, 1Password, gleich welche)
          ein Konto hinzufügen und diesen Code scannen. Es geht dabei nichts
          an Google oder sonst jemanden: die App rechnet die Ziffern selbst
          aus, das Geheimnis bleibt auf diesem Server und auf deinem Telefon.
        </p>

        <div className="reihe" style={{ alignItems: 'flex-start', gap: 24 }}>
          <div dangerouslySetInnerHTML={{ __html: start.qr }} />

          <div style={{ flex: 1, minWidth: 260 }}>
            <p className="klein leise" style={{ marginTop: 0 }}>
              Kamera streikt? Dann in der App "Schlüssel eingeben" wählen und
              abtippen:
            </p>
            <code className="faktorschluessel" style={{
              display: 'block', padding: '8px 10px', wordBreak: 'break-all',
              border: '1px solid var(--rand)', borderRadius: 6, fontSize: 14,
            }}>{start.geheimnis}</code>
            <div className="reihe" style={{ marginTop: 8, gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="knopf"
                      onClick={() => schluesselKopieren(start.geheimnis)}>
                {kopiert ? 'Kopiert' : 'Schlüssel kopieren'}
              </button>
              {/* DIREKT IN DIE APP: ein `otpauth:`-Link öffnet auf dem
                  Telefon die Authenticator-App mit dem fertigen Eintrag.
                  Kein Abtippen, kein Kopieren. */}
              {amTelefon() && (
                <a className="knopf" href={start.adresse}>In der App öffnen</a>
              )}
            </div>

            <form onSubmit={bestaetigen} style={{ marginTop: 18 }}>
              <label htmlFor="ersterCode" style={{ display: 'block' }}>
                Und jetzt den Code, den die App anzeigt
              </label>
              <input
                id="ersterCode" autoFocus={!amTelefon()} inputMode="numeric"
                autoComplete="one-time-code" placeholder="123456"
                value={code}
                /* Eingefügt wird oft „899 913" oder mit Zeilenumbruch: nur
                   die Ziffern zählen, höchstens 6. */
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ fontSize: 20, letterSpacing: 3, width: 160,
                         textAlign: 'center' }}
              />
              {fehler && (
                <div style={{ marginTop: 8 }}>
                  <Fehlerkasten lage={fehler} />
                  <p className="klein leise" style={{ marginBottom: 0 }}>
                    Passt es immer noch nicht, steht in der App vielleicht ein
                    älterer Eintrag. Dann den Eintrag dort löschen und mit
                    einem neuen Schlüssel beginnen:{' '}
                    <button type="button" className="knopf klein" onClick={() => {
                      setFehler(null); setCode(''); setKopiert(false);
                      api.faktorEinrichten(true).then(setStart)
                        .catch((f) => setFehler(ausFehler(f)));
                    }}>Neuer Schlüssel</button>
                  </p>
                </div>
              )}
              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt" disabled={laeuft || code.length < 6}>
                  {laeuft ? 'Einen Moment' : 'Einrichten'}
                </button>
                {abbrechen && (
                  <button type="button" className="knopf" onClick={abbrechen}>
                    Später
                  </button>
                )}
              </div>
              <p className="klein leise" style={{ marginBottom: 0 }}>
                Erst wenn dieser Code stimmt, gilt der Faktor. Vorher ändert
                sich nichts an deiner Anmeldung: wer den QR-Code falsch
                abfotografiert hat, sperrt sich damit nicht aus.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}


/** Die eigene Sicherheit: Faktoren, Geräte, Wiederherstellung. */
/**
 * Das eigene Profilbild.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „für alle benutzer die möglichkeit schaffen, dass man ein profilbild
 *    hochladen kann, das soll dann unten links in dem kreis angezeigt werden
 *    bzw bei der auftragsinfo bei den benutzern."
 *
 * ES STEHT UNTER „MEINE ANMELDUNG", und dort gehört es hin: dieser Bildschirm
 * gehört jedem, der sich anmeldet - der Monteur erreicht ihn genauso wie der
 * Innendienst. Ein eigener Menüpunkt für ein Bild wäre einer zu viel.
 *
 * UND ES GEHT DURCH DIESELBE FLÄCHE WIE JEDE ANDERE DATEI. Es gibt in diesem
 * Haus GENAU EIN Dateifeld (`teile/Abwurf.tsx`); ein zweites hier wäre die
 * fünfte Bauart für eine Handbewegung.
 */
function MeinBild({ ich, geaendert }:
  { ich: Anmelder | null; geaendert?: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  /* DER STEMPEL IST KEIN SCHMUCK. Der Leseweg gibt eine Stunde
     Zwischenspeicher mit; ohne einen neuen Wert in der Adresse zeigt der
     Browser nach dem Hochladen weiter das alte Bild, und wer es gerade
     ausgetauscht hat, sucht den Fehler an der falschen Stelle. */
  const [stempel, setStempel] = useState(() => Date.now());
  const [hat, setHat] = useState(!!ich?.hatBild);

  if (!ich) return null;

  async function hoch(datei: File) {
    setFehler(null); setMeldung(null); setLaeuft(true);
    try {
      const a = await api.profilbildHochladen(datei);
      setHat(true); setStempel(Date.now());
      setMeldung(a?.bericht ? `${a.meldung} (${a.bericht})` : a?.meldung ?? null);
      geaendert?.();
    } catch (e) { setFehler(ausFehler(e)); }
    finally { setLaeuft(false); }
  }

  async function weg() {
    setFehler(null); setMeldung(null); setLaeuft(true);
    try {
      await api.profilbildWeg();
      setHat(false); setStempel(Date.now());
      setMeldung('Das Bild ist entfernt. Es gelten wieder Ihre Initialen.');
      geaendert?.();
    } catch (e) { setFehler(ausFehler(e)); }
    finally { setLaeuft(false); }
  }

  return (
    <div className="karte">
      <h3>Mein Bild</h3>
      <div className="leib">
        <Fehlerkasten lage={fehler} />
        {meldung && <div className="hinweis">{meldung}</div>}
        <div className="reihe" style={{ alignItems: 'flex-start', gap: 14 }}>
          <Gesicht benutzerId={ich.benutzerId} name={ich.name}
                   hatBild={hat} stempel={stempel} gross />
          <div className="wachsen" style={{ minWidth: 0 }}>
            <Abwurf
              klein
              titel={hat ? 'Ein anderes Bild ablegen' : 'Bild ablegen'}
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/*"
              laeuft={laeuft}
              laufText="Wird hochgeladen"
              hinweis={'PNG, JPG oder WEBP, höchstens 8 MB. Ein Foto vom '
                     + 'iPhone (HEIC) wird beim Hochladen gewandelt und auf '
                     + '256 Pixel gebracht.'}
              nimm={(stuecke) => { if (stuecke[0]) hoch(stuecke[0].datei); }}
            />
            {hat && (
              <button className="knopf" style={{ marginTop: 8 }}
                      disabled={laeuft} onClick={weg}>
                Bild entfernen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Einen Passkey einrichten.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „ja, baue Passkey (WebAuthn) auch noch daneben."
 *
 * ER STEHT NEBEN DER APP UND NICHT AN IHRER STELLE. Wer kein passendes Gerät
 * hat, richtet weiter die App ein; ein Haus mit nur einem Weg herein sperrt
 * beim ersten kaputten Telefon jemanden aus.
 *
 * UND WO ER NICHT GEHT, STEHT ER NICHT DA. Ein Browser ohne WebAuthn bekommt
 * den Knopf gar nicht erst zu sehen - ein Knopf, der auf eine Fehlermeldung
 * führt, ist schlimmer als kein Knopf.
 */
function PasskeyKnopf({ laden, setMeldung }:
  { laden: () => void; setMeldung: (s: string | null) => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (!passkeyMoeglich()) return null;

  async function los() {
    setFehler(null); setMeldung(null); setLaeuft(true);
    try {
      const start = await api.passkeyAnlegenStart();
      const antwort = await passkeyAnlegen(start);
      /* DER NAME KOMMT VOM GERÄT, NICHT AUS EINEM FORMULAR. „Chrome auf
         Windows" sagt beim Aufräumen mehr als „Passkey 2", und ein
         Pflichtfeld vor dem Fingerabdruck wäre ein Handgriff zu viel. */
      const name = navigator.userAgent.includes('Macintosh') ? 'Mac'
        : navigator.userAgent.includes('iPhone') ? 'iPhone'
        : navigator.userAgent.includes('Android') ? 'Android'
        : navigator.userAgent.includes('Windows') ? 'Windows'
        : 'Passkey';
      const a = await api.passkeyBestaetigen({ ...antwort,
        aufgabe: start.aufgabe, bezeichnung: `Passkey (${name})` });
      setMeldung(`„${a.bezeichnung}" ist eingerichtet. Beim nächsten Anmelden `
               + 'steht neben dem Code der Knopf „Mit Passkey anmelden".');
      laden();
    } catch (f: any) {
      setFehler(f?.name ? passkeyFehler(f) : (f?.message ?? 'Es ging nicht.'));
    } finally { setLaeuft(false); }
  }

  return (
    <>
      <button className="knopf" disabled={laeuft} onClick={los}>
        {laeuft ? 'Einen Moment' : 'Passkey einrichten'}
      </button>
      {fehler && (
        <div className="hinweis fehler" style={{ marginTop: 8 }}>{fehler}</div>
      )}
    </>
  );
}

export function MeineSicherheit({ ich, geaendert }:
  { ich?: Anmelder | null; geaendert?: () => void }) {
  const [stand, setStand] = useState<any>(null);
  const [geraete, setGeraete] = useState<any[]>([]);
  const [neu, setNeu] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function laden() {
    setStand(await api.faktorStand());
    setGeraete(await api.faktorGeraete().catch(() => []));
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  if (!stand) return null;

  if (neu) {
    return (
      <FaktorEinrichtung
        fertig={() => { setNeu(false); laden(); }}
        abbrechen={() => setNeu(false)}
      />
    );
  }

  return (
    <>
      <MeinBild ich={ich ?? null} geaendert={geaendert} />
      <MeinPasswort ich={ich ?? null} />
      <MeineSitzungen />
    <div className="karte">
      <h3>2FA (zweiter Faktor)</h3>
      <div className="leib">
        {meldung && <div className="hinweis">{meldung}</div>}

        {stand.faktoren.filter((f: any) => f.bestaetigt_am).length === 0 ? (
          <>
            <p className="klein" style={{ marginTop: 0 }}>
              {stand.pflicht
                ? 'Für deinen Zugang ist ein zweiter Faktor vorgeschrieben. '
                  + 'Solange keiner eingerichtet ist, schützt nur das Passwort.'
                : 'Bisher schützt nur das Passwort. Ein zweiter Faktor macht '
                  + 'ein gestohlenes Passwort wertlos.'}
            </p>
            <button className="knopf haupt" onClick={() => setNeu(true)}>
              Authenticator-App einrichten
            </button>
            <PasskeyKnopf laden={laden} setMeldung={setMeldung} />
          </>
        ) : (
          <>
            <table className="liste">
              <thead>
                <tr><th style={{ width: 220 }}>Verfahren</th>
                <th style={{ width: 160 }}>eingerichtet</th>
                <th style={{ width: 160 }}>zuletzt benutzt</th><th /></tr>
              </thead>
              <tbody>
                {stand.faktoren.filter((f: any) => f.bestaetigt_am).map((f: any) => (
                  <tr key={f.id} style={{ cursor: 'default' }}>
                    <td><b>{f.bezeichnung}</b>
                      <div className="klein leise">{f.art}</div></td>
                    <td className="klein leise">
                      {datum(f.bestaetigt_am)}
                    </td>
                    <td className="klein leise">
                      {f.zuletzt_benutzt
                        ? zeitpunkt(f.zuletzt_benutzt)
                        : 'noch nie'}
                    </td>
                    <td>
                      <button className="knopf klein" onClick={async () => {
                        if (!await fragen('Diesen Faktor wirklich entfernen?')) return;
                        await api.faktorEntfernen(f.id);
                        laden();
                      }}>entfernen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="klein leise" style={{ marginTop: 12 }}>
              Noch {stand.wiederherstellungscodes} unbenutzte
              Wiederherstellungscodes.
              {stand.wiederherstellungscodes < 3 && (
                <b> Das wird knapp. Faktor neu einrichten, dann gibt es 10 neue.</b>
              )}
            </p>

            <h4 style={{ marginBottom: 6 }}>
              Geräte, die den Code nicht mehr fragen ({geraete.length})
            </h4>
            {geraete.length === 0 ? (
              <p className="klein leise" style={{ marginTop: 0 }}>
                Keins. Jede Anmeldung fragt den Code.
              </p>
            ) : (
              <table className="liste">
                <tbody>
                  {geraete.map((g) => (
                    <tr key={g.id} style={{ cursor: 'default' }}>
                      <td className="klein">{(g.bezeichnung ?? 'unbekannt').slice(0, 70)}</td>
                      <td className="klein leise">{g.ip}</td>
                      <td className="klein leise">
                        bis {datum(g.gilt_bis)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="reihe" style={{ marginTop: 12 }}>
              <button className="knopf" onClick={async () => {
                const a = await api.faktorGeraeteWiderrufen();
                setMeldung(`${a.widerrufen} Gerät(e) widerrufen. Jede Anmeldung `
                         + 'fragt jetzt wieder nach dem Code.');
                laden();
              }}>
                Telefon verloren: alle Geräte widerrufen
              </button>
              <button className="knopf" onClick={() => setNeu(true)}>
                Weiteren Faktor einrichten
              </button>
              <PasskeyKnopf laden={laden} setMeldung={setMeldung} />
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}

/**
 * Sein eigenes Passwort ändern.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „Niemand bekommt einen extra Link. Alle loggen sich mit ihren Zugangsdaten
 *    ein und aufgrund der Zuordnung und Rechten sieht er dann das was er sehen
 *    muss."
 *
 * Wenn das Passwort der einzige Weg herein ist, muss jeder seines ändern
 * können. Vorher ging das NIRGENDS: weder in der Oberfläche noch für einen
 * selbst auf der Kommandozeile. Wer ein vom Innendienst gesetztes Passwort
 * bekam, behielt es.
 *
 * DAS ALTE GEHÖRT DAZU, auch wenn man schon angemeldet ist. Ein offener
 * Rechner in der Kaffeeküche ist der Regelfall, nicht die Ausnahme.
 */
/* AUCH DIE WARTESEITE BRAUCHT DIESE KARTE, deshalb ausgeführt statt lokal.
   Ein zweites Passwortformular daneben wäre die dritte Fassung derselben
   Sache - zwei gibt es schon (hier und der Wechselzwang), und die dritte
   liefe beim nächsten Nachtrag auseinander. */
export function MeinPasswort({ ich }: { ich: Anmelder | null }) {
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [nochmal, setNochmal] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [fertig, setFertig] = useState<number | null>(null);
  const [verstoesse, setVerstoesse] = useState<string[]>([]);

  /* DIE MINDESTLAENGE KOMMT VOM SERVER. Hier stand `10`, festverdrahtet, an
     zwei Stellen - und der Satz darunter sagte „zehn Zeichen", als die
     Vorgabe längst zwölf verlangte. */
  const vorgabe = usePasswortvorgabe();
  const mindest = vorgabe?.mindestlaenge ?? 12;
  const kurz = neu.length > 0 && neu.length < mindest;
  const ungleich = nochmal.length > 0 && neu !== nochmal;
  const bereit = alt.length > 0 && neu.length >= mindest && neu === nochmal;

  async function aendern() {
    setArbeitet(true); setFehler(null); setFertig(null); setVerstoesse([]);
    try {
      const a = await api.meinPasswort(alt, neu);
      setFertig(a.beendeteSitzungen);
      setAlt(''); setNeu(''); setNochmal('');
    } catch (f: any) {
      setFehler(ausFehler(f));
      setVerstoesse(f?.daten?.verstoesse ?? []);
    } finally { setArbeitet(false); }
  }

  return (
    <div className="karte">
      <h3>Mein Passwort</h3>
      <div className="leib">
        <Fehlerkasten lage={fehler} />
        {fertig !== null && (
          <div className="hinweis">
            <b>Das Passwort ist geändert.</b>
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              {fertig === 0
                ? 'Es lief keine weitere Sitzung.'
                : `${fertig} weitere ${fertig === 1 ? 'Sitzung wurde'
                    : 'Sitzungen wurden'} beendet. Diese hier bleibt offen.`}
            </div>
          </div>
        )}
        {/* DIE VORGABE STEHT DA, BEVOR GETIPPT WIRD. Marvin, 27.08.2026:
            „das muss auch angezeigt werden bei jeder passwortvergabe." */}
        <Passwortregeln wert={neu} verstoesse={verstoesse} />
        <p className="klein leise" style={{ marginTop: 8 }}>
          Wer sein bisheriges nicht mehr weiß, lässt sich im Büro ein neues
          setzen - muss es dann aber beim nächsten Anmelden gleich ändern.
        </p>
        {/* DAS BENUTZERFELD, UND ES IST KEIN SCHMUCK.
            ORIGINALTEXT MARVIN (26.08.2026)
              „hier ist ein fehler, wenn ich auf meine anmeldung klicke, dann
               springt er im hintergrund auch ,aufträge' und sucht nach meiner
               emailadresse."

            DER PASSWORTMANAGER DES BROWSERS WAR ES. Er erkennt ein
            Passwortformular an den drei Feldern darunter, will Benutzername
            UND Passwort ausfüllen - und findet hier keinen Benutzernamen.
            Dann nimmt er das nächstgelegene Textfeld der SEITE, und das war
            die Suche der Arbeitsliste hinter dem Fenster. Ergebnis: die
            Mailadresse stand in der Suche, die Arbeitsliste zeigte „0 von 0",
            und es sah aus, als wären alle Aufträge weg.

            Ein `autocomplete="off"` auf der Suche allein hätte nicht
            gereicht: Chrome ignoriert es bei Anmeldeformularen bewusst. Was
            hilft, ist ein Feld, das der Manager füllen DARF. Es steht
            sichtbar da und beantwortet nebenbei eine echte Frage: unter
            welchem Zugang bin ich hier eigentlich angemeldet. */}
        <div className="feld">
          <label htmlFor="mp-benutzer">Mailadresse</label>
          <input id="mp-benutzer" type="email" name="username" readOnly
                 autoComplete="username" value={ich?.email ?? ''} />
        </div>
        <div className="feld">
          <label htmlFor="mp-alt">Bisheriges Passwort</label>
          <input id="mp-alt" type="password" value={alt}
                 autoComplete="current-password"
                 onChange={(e) => setAlt(e.target.value)} />
        </div>
        <div className="feld">
          <label htmlFor="mp-neu">Neues Passwort</label>
          <input id="mp-neu" type="password" value={neu}
                 autoComplete="new-password"
                 onChange={(e) => setNeu(e.target.value)} />
          {/* DIE ZAHL KOMMT AUS DERSELBEN VORGABE WIE DIE BEDINGUNG DARÜBER.
              Hier stand `10 - neu.length`, während `kurz` gegen `mindest`
              prüft: bei fünfzehn Zeichen Vorgabe und zwölf getippten stand
              „Noch -2 Zeichen." unter einem Feld, das der Server abweist.
              **Eine Zahl, die man nicht erklären kann, nimmt der Anzeige
              daneben den Glauben.** */}
          {kurz && (
            <div className="klein rot">Noch {mindest - neu.length} Zeichen.</div>
          )}
        </div>
        <div className="feld">
          <label htmlFor="mp-nochmal">Neues Passwort wiederholen</label>
          <input id="mp-nochmal" type="password" value={nochmal}
                 autoComplete="new-password"
                 onChange={(e) => setNochmal(e.target.value)} />
          {ungleich && (
            <div className="klein rot">Die beiden sind nicht gleich.</div>
          )}
        </div>
        <p className="klein leise">
          Alle anderen Sitzungen enden dabei, diese hier bleibt offen. Wer sein
          Passwort ändert, weil jemand anderes drin ist, will genau das.
        </p>
        <button className="knopf haupt" disabled={!bereit || arbeitet}
                onClick={aendern}>
          {arbeitet ? 'Einen Moment' : 'Passwort ändern'}
        </button>
      </div>
    </div>
  );
}


/**
 * Die eigenen Anmeldungen, einzeln zu beenden.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „Niemand bekommt einen extra Link. Alle loggen sich mit ihren Zugangsdaten
 *    ein und aufgrund der Zuordnung und Rechten sieht er dann das was er sehen
 *    muss."
 *
 * WENN DAS PASSWORT DER EINZIGE WEG HEREIN IST, IST DIE OFFENE SITZUNG DER
 * ZWEITE. `GET /ich` liefert diese Liste seit Migration 001, und
 * `DELETE /ich/sitzung/:id` steht seit ebenso langem da - **gerufen hat beides
 * keine einzige Zeile der Oberfläche.** Ein Schema ohne Code ist eine
 * Behauptung, und hier war es eine über die Sicherheit des eigenen Zugangs.
 *
 * WARUM DAS NICHT DASSELBE IST WIE „PASSWORT ÄNDERN": das ist die grobe Axt.
 * Sie beendet ALLE anderen Sitzungen und verlangt ein neues Passwort - und
 * wer nur das Tablet abmelden will, das im Büro liegt, muss dafür weder sein
 * Passwort wechseln noch das Telefon des Kollegen aussperren, der gerade auf
 * der Baustelle steht.
 *
 * UND DIE EIGENE ZEILE IST GEKENNZEICHNET, WEIL DER SERVER ES SAGT. Die
 * Oberfläche kann sie nicht erkennen: das Sitzungscookie ist `httpOnly`, und
 * das soll es bleiben. Drei Zeilen „Chrome auf Windows" untereinander, und
 * die falsche beendet, hiesse sich selbst aussperren.
 */
function MeineSitzungen() {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [arbeitet, setArbeitet] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function laden() {
    const a = await api.ich();
    setZeilen((a as any).sitzungen ?? []);
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  async function beenden(z: any) {
    setArbeitet(z.id); setFehler(null); setMeldung(null);
    try {
      const a = await api.sitzungBeenden(z.id);
      setMeldung(`„${a.geraet ?? geraetText(z.geraet)}" ist abgemeldet.`);
      await laden();
    } catch (f: any) {
      setFehler(ausFehler(f));
      /* AUCH NACH EINEM FEHLER WIRD NEU GELADEN. Der häufigste Grund ist
         eine Sitzung, die inzwischen abgelaufen ist - dann ist die Liste
         selbst der Teil, der nicht mehr stimmt. */
      await laden().catch(() => {});
    } finally { setArbeitet(null); }
  }

  if (!zeilen) return null;

  return (
    <div className="karte">
      <h3>Meine Anmeldungen ({zeilen.length})</h3>
      <div className="leib">
        <Fehlerkasten lage={fehler} />
        {meldung && <div className="hinweis">{meldung}</div>}

        {zeilen.length === 0 ? (
          /* Diesen Fall gibt es fast nie - wer das liest, ist angemeldet.
             Er steht trotzdem da: eine Kopfzeile über nichts sieht aus wie
             ein Bildschirm, der noch lädt. */
          <p className="klein leise" style={{ marginTop: 0 }}>
            Es läuft keine Anmeldung. Das kann eigentlich nicht sein - beim
            nächsten Neuladen steht es richtig da.
          </p>
        ) : (
          <table className="liste">
            <thead>
              <tr>
                <th>Gerät</th>
                <th style={{ width: 150 }}>angemeldet seit</th>
                <th style={{ width: 150 }}>läuft ab</th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z: any) => (
                <tr key={z.id} style={{ cursor: 'default' }}>
                  <td className="klein">
                    {geraetText(z.geraet)}
                    {z.aktuell && (
                      <div className="klein leise">
                        Das ist dieses Gerät.
                      </div>
                    )}
                  </td>
                  <td className="klein leise">{zeitpunkt(z.angelegt_am)}</td>
                  <td className="klein leise">{zeitpunkt(z.gilt_bis)}</td>
                  <td className="knopfleiste">
                    {/* DIE EIGENE ZEILE TRÄGT KEINEN KNOPF, und der Riegel
                        dahinter liegt trotzdem im Server: eine Anzeige ist
                        eine Höflichkeit, keine Sicherung. Statt des Knopfes
                        steht der WEG da, den man wirklich meint. */}
                    {z.aktuell ? (
                      <span className="klein leise">über „Abmelden"</span>
                    ) : (
                      <button className="knopf klein"
                              disabled={arbeitet !== null}
                              onClick={() => beenden(z)}>
                        {arbeitet === z.id ? '…' : 'abmelden'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="klein leise" style={{ marginBottom: 0 }}>
          Hier steht jedes Gerät, auf dem dieser Zugang gerade angemeldet ist.
          Steht eines darunter, das nicht deins ist, beende es und
          <b> ändere danach dein Passwort</b> - sonst meldet sich derselbe
          wieder an.
        </p>
      </div>
    </div>
  );
}

/**
 * Was in der Spalte „Gerät" steht.
 *
 * `sitzung.geraet` trägt die Browserkennung, und die ist hundertzwanzig
 * Zeichen lang und für einen Menschen unlesbar. Gekürzt wird sie in der
 * ANZEIGE und mit dem Rest im Titel - **ein abgeschnittener Text, dessen
 * Rest nirgends steht, ist eine halbe Auskunft**, und hier hängt an ihr die
 * Frage, ob jemand Fremdes drin ist.
 *
 * Und eine leere Angabe wird nicht verschwiegen: „unbekanntes Gerät" ist
 * eine Auskunft, eine leere Zelle sieht nach einem Anzeigefehler aus.
 */
function geraetText(g: string | null | undefined): string {
  const s = String(g ?? '').trim();
  if (!s) return 'unbekanntes Gerät';
  return s.length <= 60 ? s : s.slice(0, 60) + '…';
}
